import { createVoucherCode } from '@/marketing/lib/voucherCode';
import type {
  CommissionTierRates,
  GenerateVouchersRequest,
  GenerateVouchersResult,
  ImportVouchersRequest,
  ImportVouchersResult,
  InfluencerVoucher,
  SetVoucherCommissionRequest,
  SetVoucherStatusRequest,
  VoucherListParams,
  VoucherListing,
  VoucherMutationResult,
  VoucherPerformance,
  VoucherPerformanceOrder,
  SetCommissionTierRatesRequest,
  VoucherSort,
} from '@/marketing/models/voucher';

// In-memory stand-in for the internal-api COUPON endpoints, shaped exactly like the real contract:
// decimal-string money, uses separate from orderCount, server-side filtering/sorting/paging.

interface MockHubAccount {
  userId: number;
  handle: string;
  platform: string;
  displayName: string;
}

const MOCK_HUB_ACCOUNTS: MockHubAccount[] = [
  { userId: 18201, handle: 'sora.skin', platform: 'tiktok', displayName: 'Sora Kim' },
  { userId: 18202, handle: 'minji.glow', platform: 'tiktok', displayName: 'Minji Park' },
  { userId: 18203, handle: 'hana.beauty', platform: 'instagram', displayName: 'Hana Sato' },
  { userId: 18204, handle: 'lilyskincare', platform: 'tiktok', displayName: 'Lily Tran' },
  { userId: 18205, handle: 'jadeglowup', platform: 'tiktok', displayName: 'Jade Wu' },
  { userId: 18207, handle: 'glowwithmei', platform: 'instagram', displayName: 'Mei Chen' },
  { userId: 18208, handle: 'dewy.days', platform: 'tiktok', displayName: 'Aria Lopez' },
];

const MOCK_CURRENCY = 'USD';
const MOCK_LATENCY_MS = 250;
const DEFAULT_DISCOUNT_PERCENT = 5;
const DEFAULT_LIMIT = 50;

const money = (value: number): string => value.toFixed(2);

const delay = <T>(value: T): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(value), MOCK_LATENCY_MS));

const identityKey = (handle: string, platform: string): string =>
  `${platform.toLowerCase()}/${handle.replace(/^@/, '').toLowerCase()}`;

const seededVoucher = (
  account: MockHubAccount,
  code: string,
  overrides: Partial<InfluencerVoucher>,
): InfluencerVoucher => ({
  code,
  handle: account.handle,
  platform: account.platform,
  displayName: account.displayName,
  userId: account.userId,
  discountPercent: DEFAULT_DISCOUNT_PERCENT,
  uses: 0,
  orderCount: 0,
  clicks: 0,
  utmOrders: 0,
  revenueTotal: money(0),
  commissionTotal: money(0),
  status: 'active',
  source: 'generated',
  createdAt: '2026-06-02T09:00:00.000Z',
  ...overrides,
});

const vouchers: InfluencerVoucher[] = [
  seededVoucher(MOCK_HUB_ACCOUNTS[0]!, 'soraskin5kr', {
    uses: 26,
    orderCount: 23,
    clicks: 341,
    utmOrders: 9,
    revenueTotal: money(1284),
    commissionTotal: money(128.4),
  }),
  seededVoucher(MOCK_HUB_ACCOUNTS[1]!, 'minjiglow10te', {
    discountPercent: 10,
    commissionPercent: 12,
    uses: 47,
    orderCount: 41,
    clicks: 1024,
    utmOrders: 18,
    utmPercent: 15,
    revenueTotal: money(3120),
    commissionTotal: money(374.4),
    createdAt: '2026-05-18T09:00:00.000Z',
  }),
  seededVoucher(MOCK_HUB_ACCOUNTS[2]!, 'hanabeauty5mw', {
    uses: 9,
    orderCount: 8,
    clicks: 87,
    utmOrders: 2,
    revenueTotal: money(412),
    commissionTotal: money(41.2),
    createdAt: '2026-07-01T09:00:00.000Z',
  }),
  seededVoucher(MOCK_HUB_ACCOUNTS[3]!, 'lilyskinca15qa', {
    discountPercent: 15,
    commissionPercent: 15,
    uses: 74,
    orderCount: 67,
    clicks: 2210,
    utmOrders: 31,
    couponUtmPercent: 25,
    revenueTotal: money(5890),
    commissionTotal: money(883.5),
    createdAt: '2026-04-22T09:00:00.000Z',
  }),
  seededVoucher(MOCK_HUB_ACCOUNTS[4]!, 'jadeglowup5xt', {
    status: 'disabled',
    createdAt: '2026-07-28T09:00:00.000Z',
  }),
  seededVoucher(MOCK_HUB_ACCOUNTS[4]!, 'jadeglowsummer12', {
    discountPercent: 12,
    campaignId: 'mock-affiliate-summer',
    campaignName: 'Summer Glow Affiliate',
    createdAt: '2026-10-10T09:00:00.000Z',
  }),
  seededVoucher(MOCK_HUB_ACCOUNTS[3]!, 'winterbarrier10', {
    discountPercent: 10,
    campaignId: 'mock-affiliate-winter',
    campaignName: 'Winter Barrier Affiliate',
    createdAt: '2026-10-12T09:00:00.000Z',
  }),
  {
    // Imported from Upfluence, never claimed: the creator has no Creator Hub account yet.
    code: 'GLOW_DEVINA',
    handle: 'devina',
    platform: 'tiktok',
    discountPercent: 5,
    commissionPercent: 8,
    uses: 12,
    orderCount: 11,
    clicks: 0,
    utmOrders: 0,
    revenueTotal: money(742.5),
    commissionTotal: money(59.4),
    status: 'active',
    source: 'imported',
    createdAt: '2026-08-14T09:00:00.000Z',
  },
];

const isCodeTaken = (candidateCode: string): boolean =>
  vouchers.some((voucher) => voucher.code.toLowerCase() === candidateCode.toLowerCase());

const findVoucher = (code: string): InfluencerVoucher | undefined =>
  vouchers.find((voucher) => voucher.code.toLowerCase() === code.toLowerCase());

const matchesSearch = (voucher: InfluencerVoucher, search: string): boolean => {
  const needle = search.toLowerCase();
  return voucher.code.toLowerCase().includes(needle) || voucher.handle.toLowerCase().includes(needle);
};

const SORT_COMPARATORS: Record<VoucherSort, (left: InfluencerVoucher, right: InfluencerVoucher) => number> = {
  createdAt: (left, right) => right.createdAt.localeCompare(left.createdAt),
  code: (left, right) => left.code.localeCompare(right.code),
  orders: (left, right) => right.orderCount - left.orderCount,
  revenue: (left, right) => Number(right.revenueTotal) - Number(left.revenueTotal),
  commission: (left, right) => Number(right.commissionTotal) - Number(left.commissionTotal),
};

const sumBy = (rows: InfluencerVoucher[], read: (voucher: InfluencerVoucher) => number): number =>
  rows.reduce((total, voucher) => total + read(voucher), 0);

const toMutationResult = (voucher: InfluencerVoucher): VoucherMutationResult => ({
  code: voucher.code,
  handle: voucher.handle,
  platform: voucher.platform,
  ...(voucher.userId !== undefined ? { userId: voucher.userId } : {}),
  discountPercent: voucher.discountPercent,
  ...(voucher.commissionPercent !== undefined ? { commissionPercent: voucher.commissionPercent } : {}),
  ...(voucher.utmPercent !== undefined ? { utmPercent: voucher.utmPercent } : {}),
  ...(voucher.couponUtmPercent !== undefined ? { couponUtmPercent: voucher.couponUtmPercent } : {}),
  status: voucher.status,
  source: voucher.source,
  createdAt: voucher.createdAt,
  updatedAt: new Date().toISOString(),
});

function buildWeekly(voucher: InfluencerVoucher): VoucherPerformance['weekly'] {
  const revenue = Number(voucher.revenueTotal);
  const commissionRate = (voucher.commissionPercent ?? tierRates.couponPercent) / 100;
  const weekCount = 12;

  return Array.from({ length: weekCount }, (_, index) => {
    const weekRevenue = (revenue * (index + 1)) / ((weekCount * (weekCount + 1)) / 2);
    return {
      weekStart: new Date(Date.UTC(2026, 4, 25 + index * 7)).toISOString().slice(0, 10),
      orderCount: Math.round((voucher.orderCount * (index + 1)) / ((weekCount * (weekCount + 1)) / 2)),
      revenue: money(weekRevenue),
      commission: money(weekRevenue * commissionRate),
    };
  });
}

function buildOrders(voucher: InfluencerVoucher): VoucherPerformanceOrder[] {
  const orderCount = Math.min(voucher.orderCount, 6);
  const perOrderRevenue = orderCount > 0 ? Number(voucher.revenueTotal) / voucher.orderCount : 0;
  const commissionRate = (voucher.commissionPercent ?? tierRates.couponPercent) / 100;

  return Array.from({ length: orderCount }, (_, index) => ({
    orderId: 48213 - index * 111,
    date: new Date(Date.UTC(2026, 7, 9 - index * 4)).toISOString(),
    revenue: money(perOrderRevenue),
    commission: money(perOrderRevenue * commissionRate),
    status: index === 0 ? ('pending' as const) : index === 1 ? ('refunded' as const) : ('confirmed' as const),
    settled: index > 2,
  }));
}

// Defaults follow the marketing tier table: coupon-only 5, link-only 10, link+coupon 20.
let tierRates: CommissionTierRates = {
  version: 1,
  couponPercent: 5,
  utmPercent: 10,
  couponUtmPercent: 20,
};

export const mockVoucherAPIClient = {
  listVouchers(params: VoucherListParams): Promise<VoucherListing> {
    const search = params.search?.trim() ?? '';
    const filtered = vouchers
      .filter((voucher) => (params.status ? voucher.status === params.status : true))
      .filter((voucher) => (params.campaignId ? voucher.campaignId === params.campaignId : true))
      .filter((voucher) =>
        params.codeGroup === 'personal' ? !voucher.campaignId : params.codeGroup === 'campaign' ? Boolean(voucher.campaignId) : true,
      )
      .filter((voucher) => (search.length > 0 ? matchesSearch(voucher, search) : true));

    const sorted = [...filtered].sort(SORT_COMPARATORS[params.sort ?? 'createdAt']);
    const limit = params.limit ?? DEFAULT_LIMIT;
    const offset = params.offset ?? 0;

    return delay({
      currency: MOCK_CURRENCY,
      totals: {
        activeCodes: filtered.filter((voucher) => voucher.status === 'active').length,
        disabledCodes: filtered.filter((voucher) => voucher.status === 'disabled').length,
        redemptions: sumBy(vouchers, (voucher) => voucher.uses),
        orderCount: sumBy(filtered, (voucher) => voucher.orderCount),
        revenueTotal: money(sumBy(filtered, (voucher) => Number(voucher.revenueTotal))),
        commissionTotal: money(sumBy(filtered, (voucher) => Number(voucher.commissionTotal))),
        commissionAccrued: money(sumBy(filtered, (voucher) => Number(voucher.commissionTotal)) * 0.6),
      },
      codes: sorted.slice(offset, offset + limit),
      total: filtered.length,
      limit,
      offset,
    });
  },

  getPerformance(code: string): Promise<VoucherPerformance> {
    const voucher = findVoucher(code);
    if (!voucher) {
      return Promise.reject(new Error(`Influencer coupon ${code} does not exist`));
    }

    return delay({
      code: voucher.code,
      handle: voucher.handle,
      platform: voucher.platform,
      ...(voucher.displayName !== undefined ? { displayName: voucher.displayName } : {}),
      discountPercent: voucher.discountPercent,
      ...(voucher.commissionPercent !== undefined ? { commissionPercent: voucher.commissionPercent } : {}),
      ...(voucher.utmPercent !== undefined ? { utmPercent: voucher.utmPercent } : {}),
      ...(voucher.couponUtmPercent !== undefined ? { couponUtmPercent: voucher.couponUtmPercent } : {}),
      status: voucher.status,
      createdAt: voucher.createdAt,
      currency: MOCK_CURRENCY,
      orderCount: voucher.orderCount,
      revenueTotal: voucher.revenueTotal,
      commissionTotal: voucher.commissionTotal,
      weekly: buildWeekly(voucher),
      orders: buildOrders(voucher),
    });
  },

  generateVouchers(request: GenerateVouchersRequest): Promise<GenerateVouchersResult> {
    const discountPercent = request.discountPercent ?? DEFAULT_DISCOUNT_PERCENT;
    const result: GenerateVouchersResult = { generated: 0, alreadyHadCode: 0, skipped: 0, errors: [] };

    for (const target of request.targets) {
      const account = MOCK_HUB_ACCOUNTS.find((candidate) => candidate.userId === target.userId);
      if (!account) {
        result.skipped++;
        result.errors.push(`userId=${target.userId}: no connected social account to derive a handle`);
        continue;
      }
      if (vouchers.some((voucher) => voucher.userId === target.userId)) {
        result.alreadyHadCode++;
        continue;
      }

      vouchers.push(
        seededVoucher(account, createVoucherCode({ handle: account.handle, discountPercent, isCodeTaken }), {
          discountPercent,
          ...(target.commissionPercent !== undefined ? { commissionPercent: target.commissionPercent } : {}),
          createdAt: new Date().toISOString(),
        }),
      );
      result.generated++;
    }

    return delay(result);
  },

  importVouchers(request: ImportVouchersRequest): Promise<ImportVouchersResult> {
    const fallbackDiscount = request.discountPercent ?? DEFAULT_DISCOUNT_PERCENT;
    const result: ImportVouchersResult = { imported: 0, unclaimed: 0, skipped: 0, errors: [] };

    for (const row of request.rows) {
      const discountPercent = row.discountPercent ?? fallbackDiscount;
      const account = MOCK_HUB_ACCOUNTS.find(
        (candidate) => identityKey(candidate.handle, candidate.platform) === identityKey(row.handle, row.platform),
      );
      const alreadyRecorded = vouchers.some(
        (voucher) => identityKey(voucher.handle, voucher.platform) === identityKey(row.handle, row.platform),
      );
      if (alreadyRecorded || (row.couponCode && isCodeTaken(row.couponCode))) {
        result.skipped++;
        continue;
      }

      const code = row.couponCode ?? createVoucherCode({ handle: row.handle, discountPercent, isCodeTaken });
      vouchers.push({
        code,
        handle: row.handle,
        platform: row.platform,
        ...(account ? { displayName: account.displayName, userId: account.userId } : {}),
        discountPercent,
        ...(row.commissionPercent !== undefined ? { commissionPercent: row.commissionPercent } : {}),
        uses: 0,
        orderCount: 0,
        clicks: 0,
        utmOrders: 0,
        revenueTotal: money(0),
        commissionTotal: money(0),
        status: 'active',
        source: 'imported',
        createdAt: new Date().toISOString(),
      });

      if (account) {
        result.imported++;
      } else {
        result.unclaimed++;
      }
    }

    return delay(result);
  },

  setVoucherStatus({ code, status }: SetVoucherStatusRequest): Promise<VoucherMutationResult> {
    const voucher = findVoucher(code);
    if (!voucher) {
      return Promise.reject(new Error(`Influencer coupon ${code} does not exist`));
    }

    voucher.status = status;
    return delay(toMutationResult(voucher));
  },

  getCommissionTierRates(): Promise<CommissionTierRates> {
    return delay({ ...tierRates });
  },

  setCommissionTierRates(request: SetCommissionTierRatesRequest): Promise<CommissionTierRates> {
    if (request.expectedVersion !== tierRates.version) {
      return Promise.reject(new Error(`Commission tiers changed from version ${request.expectedVersion}`));
    }
    tierRates = {
      version: tierRates.version + 1,
      couponPercent: request.couponPercent,
      utmPercent: request.utmPercent,
      couponUtmPercent: request.couponUtmPercent,
    };
    return delay({ ...tierRates });
  },

  // Full replacement, like the real PATCH: a rate missing from the request clears that override.
  setVoucherCommission({
    code,
    commissionPercent,
    utmPercent,
    couponUtmPercent,
  }: SetVoucherCommissionRequest): Promise<VoucherMutationResult> {
    const voucher = findVoucher(code);
    if (!voucher) {
      return Promise.reject(new Error(`Influencer coupon ${code} does not exist`));
    }

    if (commissionPercent === undefined) delete voucher.commissionPercent;
    else voucher.commissionPercent = commissionPercent;
    if (utmPercent === undefined) delete voucher.utmPercent;
    else voucher.utmPercent = utmPercent;
    if (couponUtmPercent === undefined) delete voucher.couponUtmPercent;
    else voucher.couponUtmPercent = couponUtmPercent;

    return delay(toMutationResult(voucher));
  },
};
